import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
  isLight: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Force light theme - dark mode temporarily disabled
  const [theme, setThemeState] = useState<Theme>('light');

  // Reset localStorage to light so when dark mode is re-enabled, everyone starts fresh
  useEffect(() => {
    localStorage.setItem('crm-theme', 'light');
  }, []);

  useEffect(() => {
    // Apply dark class for Tailwind dark: variants
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Update DOM attribute for CSS selectors (manteniamo per compatibilità con CSS variables)
    document.documentElement.setAttribute('data-theme', theme);

    // Save to localStorage
    localStorage.setItem('crm-theme', theme);

    // Update html background to cover safe-area on iOS PWA
    // Using html instead of body ensures the background extends under the status bar
    if (theme === 'dark') {
      document.documentElement.style.backgroundColor = '#031834';
      document.documentElement.style.backgroundImage =
        "linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url('/Sfondo.svg')";
      // Background properties for SVG
      document.documentElement.style.backgroundAttachment = 'fixed';
      document.documentElement.style.backgroundRepeat = 'no-repeat';
      document.documentElement.style.backgroundSize = 'cover';
      document.documentElement.style.backgroundPosition = 'center';
      // Make body transparent so html background shows through
      document.body.style.backgroundColor = 'transparent';
    } else {
      // Notion-style: warm off-white background (not pure white), no image
      document.documentElement.style.backgroundColor = '#FBFBFA';
      document.documentElement.style.backgroundImage = 'none';
      // Clear all background properties for light mode
      document.documentElement.style.backgroundAttachment = '';
      document.documentElement.style.backgroundRepeat = '';
      document.documentElement.style.backgroundSize = '';
      document.documentElement.style.backgroundPosition = '';
      // Body also same warm off-white in light mode
      document.body.style.backgroundColor = '#FBFBFA';
    }
  }, [theme]);

  const toggleTheme = () => {
    setThemeState(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const setTheme = (newTheme: Theme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
    }
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
